/**
 * Matriz canónica de navegación web.
 *
 * Las rutas se autorizan por el contexto que resolvió el backend (`activeApp`)
 * y por permisos explícitos. `currentUser.type` se conserva únicamente como
 * compatibilidad para componentes legacy y nunca participa de la decisión.
 */

export const WEB_APPS = Object.freeze({
  DEALERSHIP: "dealership_web",
  GROUP: "group_web",
  DEVELOPER: "developer_web",
  RESELLER: "reseller_app",
});

export const ROUTE_RULES = Object.freeze([
  // Dealership. El orden también define la primera ruta autorizada.
  { pattern: "/", apps: [WEB_APPS.DEALERSHIP], anyOf: ["auctions.view_unit"] },
  {
    pattern: "/new-auction",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["auctions.create"],
  },
  {
    pattern: "/auctions-finished",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["auctions.view_results"],
  },
  {
    pattern: "/fast-purchase",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["auctions.view_fast_purchase"],
  },
  {
    pattern: "/modify-auction/:id",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["auctions.edit_own", "auctions.edit_unit"],
  },
  {
    pattern: "/auction-offers/:id",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["auctions.view_results"],
  },
  {
    pattern: "/new-reseller",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["resellers.create"],
  },
  {
    pattern: "/ban-reseller",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["reseller_bans.manage_unit", "reseller_bans.manage_dealership"],
    allOf: ["resellers.view"],
  },
  { pattern: "/members", apps: [WEB_APPS.DEALERSHIP], anyOf: ["members.view"] },
  {
    pattern: "/new-member",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["members.invite"],
  },
  { pattern: "/units", apps: [WEB_APPS.DEALERSHIP], anyOf: ["units.view"] },
  {
    pattern: "/units/new",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["units.create_child"],
  },
  {
    pattern: "/modify-dealership",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["dealerships.manage"],
  },
  {
    pattern: "/appraisals",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["appraisals.view_unit"],
  },
  {
    pattern: "/appraisals/:id",
    apps: [WEB_APPS.DEALERSHIP],
    // Valuar el vehículo usa `appraisals.valuate` (habilitado en dealership_web).
    // `appraisals.edit` es exclusivo de la app de peritajes y nunca está en una
    // sesión web, por lo que este detalle quedaría inaccesible.
    allOf: ["appraisals.view_unit", "appraisals.valuate"],
  },
  {
    pattern: "/appraisals-close",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["appraisals.close"],
  },
  {
    pattern: "/appraisal-cfg",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["appraisals.configure_unit"],
  },
  {
    pattern: "/vehicle-analytics",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["analytics.view_unit"],
  },
  {
    pattern: "/cars-management",
    apps: [WEB_APPS.DEALERSHIP],
    allOf: ["cars.view"],
  },
  {
    pattern: "/test-drives",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["test_drives.view_unit"],
  },
  {
    pattern: "/loans",
    apps: [WEB_APPS.DEALERSHIP],
    anyOf: ["loans.view_unit", "loans.view_own"],
  },
  {
    pattern: "/feedback-analytics",
    apps: [WEB_APPS.DEALERSHIP],
    allOf: ["test_drives.view_feedback_analytics"],
  },

  // Group.
  { pattern: "/", apps: [WEB_APPS.GROUP], anyOf: ["auctions.view_group_feed"] },
  {
    pattern: "/new-dealership",
    apps: [WEB_APPS.GROUP],
    anyOf: ["dealerships.manage"],
  },
  {
    pattern: "/modify-admins",
    apps: [WEB_APPS.GROUP],
    anyOf: ["dealerships.manage"],
  },
  {
    pattern: "/group-ban-reseller",
    apps: [WEB_APPS.GROUP],
    anyOf: ["reseller_bans.manage_dealership"],
    allOf: ["resellers.view"],
  },
  {
    pattern: "/admin-reports",
    apps: [WEB_APPS.GROUP],
    anyOf: ["analytics.view_descendants"],
  },
  {
    pattern: "/group-analytics",
    apps: [WEB_APPS.GROUP],
    anyOf: ["analytics.view_descendants"],
  },
  {
    pattern: "/auctions-finished",
    apps: [WEB_APPS.GROUP],
    anyOf: ["auctions.view_results"],
  },
  {
    pattern: "/fast-purchase",
    apps: [WEB_APPS.GROUP],
    anyOf: ["auctions.view_fast_purchase"],
  },

  // Developer/headmaster: la identidad es un contexto explícito del backend.
  // `users.disable_auth` es canónico y sólo lo posee el developer: las rutas
  // exclusivas del developer se gatean con él; las compartidas con headmaster
  // usan los permisos propios del headmaster.
  { pattern: "/", apps: [WEB_APPS.DEVELOPER], anyOf: ["users.disable_auth"] },
  {
    pattern: "/demo-requests",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["demo_requests.view"],
  },
  {
    pattern: "/new-group",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["groups.create"],
  },
  {
    pattern: "/modify-members",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["groups.manage"],
  },
  {
    pattern: "/car-analytics",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["users.disable_auth"],
  },
  {
    pattern: "/active-auction-offers",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["users.disable_auth"],
  },
  {
    pattern: "/resellers-management",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["users.disable_auth"],
  },
  {
    pattern: "/headmasters",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["users.disable_auth"],
  },
  {
    pattern: "/members-backfill",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["users.disable_auth"],
  },
  {
    pattern: "/auctions-to-cash-out",
    apps: [WEB_APPS.DEVELOPER],
    anyOf: ["cashouts.view"],
  },
  { pattern: "/groups", apps: [WEB_APPS.DEVELOPER], anyOf: ["groups.view"] },

  // Reventa usa su resolver dedicado; no toma permisos de memberships.
  { pattern: "/", apps: [WEB_APPS.RESELLER] },
  { pattern: "/my-offers", apps: [WEB_APPS.RESELLER] },
  { pattern: "/my-favorites", apps: [WEB_APPS.RESELLER] },
  { pattern: "/auction/:id", apps: [WEB_APPS.RESELLER] },
  { pattern: "/configuration", apps: [WEB_APPS.RESELLER] },
  { pattern: "/personal-data", apps: [WEB_APPS.RESELLER] },
  { pattern: "/terms", apps: [WEB_APPS.RESELLER] },
]);

export const DEALERSHIP_NAV_SECTIONS = [
  {
    id: "reventa",
    label: "Reventa",
    items: [
      {
        label: "Nuevo reventa",
        path: "/new-reseller",
        permission: ["resellers.create"],
      },
      {
        label: "Estado reventa",
        path: "/ban-reseller",
        permission: [
          "reseller_bans.manage_unit",
          "reseller_bans.manage_dealership",
        ],
      },
    ],
  },
  {
    id: "subastas",
    label: "Subastas",
    items: [
      {
        label: "Nueva subasta",
        path: "/new-auction",
        permission: ["auctions.create"],
      },
      {
        label: "Subastas finalizadas",
        path: "/auctions-finished",
        permission: ["auctions.view_results"],
      },
      {
        label: "Compras rápidas",
        path: "/fast-purchase",
        permission: ["auctions.view_fast_purchase"],
      },
      {
        label: "Analíticas de vehículos",
        path: "/vehicle-analytics",
        permission: ["analytics.view_unit"],
      },
    ],
  },
  {
    id: "appraisals",
    label: "Peritajes",
    items: [
      {
        label: "Listado de peritajes",
        path: "/appraisals",
        permission: ["appraisals.view_unit"],
      },
      {
        label: "Cerrar peritajes",
        path: "/appraisals-close",
        permission: ["appraisals.close"],
      },
      {
        label: "Configuración",
        path: "/appraisal-cfg",
        permission: ["appraisals.configure_unit"],
      },
    ],
  },
  {
    id: "testdrive",
    label: "Test drive",
    items: [
      {
        label: "Ver test drives",
        path: "/test-drives",
        permission: ["test_drives.view_unit"],
      },
      {
        label: "Préstamos",
        path: "/loans",
        permission: ["loans.view_unit", "loans.view_own"],
      },
      {
        label: "Gestión de autos",
        path: "/cars-management",
        permission: ["cars.view", "cars.manage"],
      },
      {
        label: "Analíticas feedback",
        path: "/feedback-analytics",
        permission: ["test_drives.view_feedback_analytics"],
      },
    ],
  },
  {
    id: "concesionaria",
    label: "Mi concesionaria",
    items: [
      {
        label: "Modificar concesionaria",
        path: "/modify-dealership",
        permission: ["dealerships.manage"],
      },
      { label: "Sucursales", path: "/units", permission: ["units.view"] },
      {
        label: "Nuevo miembro",
        path: "/new-member",
        permission: ["members.invite"],
      },
      { label: "Miembros", path: "/members", permission: ["members.view"] },
    ],
  },
];

export const GROUP_NAV_SECTIONS = [
  {
    id: "gestion",
    label: "Gestión",
    items: [
      { label: "Subastas finalizadas", path: "/auctions-finished" },
      { label: "Nueva concesionaria", path: "/new-dealership" },
      { label: "Administradores", path: "/modify-admins" },
      { label: "Estado Reventas", path: "/group-ban-reseller" },
    ],
  },
  {
    id: "reportes",
    label: "Reportes y analíticas",
    items: [
      { label: "Reportes por administrador", path: "/admin-reports" },
      { label: "Analíticas del grupo", path: "/group-analytics" },
      { label: "Compras rápidas", path: "/fast-purchase" },
    ],
  },
];

export const hasAnyPermission = (permissions, required) => {
  if (!required || required.length === 0) return true;
  const list = Array.isArray(required) ? required : [required];
  const owned = new Set(Array.isArray(permissions) ? permissions : []);
  return list.some((permission) => owned.has(permission));
};

export const hasAllPermissions = (permissions, required) => {
  if (!required || required.length === 0) return true;
  const list = Array.isArray(required) ? required : [required];
  const owned = new Set(Array.isArray(permissions) ? permissions : []);
  return list.every((permission) => owned.has(permission));
};

export const normalizePath = (path = "/") => {
  const raw = String(path || "/").split(/[?#]/, 1)[0];
  if (raw === "/") return raw;
  return `/${raw.split("/").filter(Boolean).join("/")}`;
};

export const matchesRoutePattern = (pattern, path) => {
  const patternParts = normalizePath(pattern).split("/").filter(Boolean);
  const pathParts = normalizePath(path).split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every(
    (part, index) => part.startsWith(":") || part === pathParts[index],
  );
};

export const findRouteRule = (path, activeApp) =>
  ROUTE_RULES.find(
    (rule) =>
      rule.apps.includes(activeApp) && matchesRoutePattern(rule.pattern, path),
  ) || null;

export const isRouteAllowed = (path, currentUser) => {
  const activeApp = currentUser?.activeApp;
  if (!activeApp) return false;

  const rule = findRouteRule(path, activeApp);
  if (!rule) return false; // deny-by-default para rutas no catalogadas

  const permissions = currentUser?.permissions || [];
  return (
    hasAnyPermission(permissions, rule.anyOf) &&
    hasAllPermissions(permissions, rule.allOf)
  );
};

export const getFirstAllowedRoute = (currentUser) => {
  if (!currentUser?.activeApp) return null;
  const seen = new Set();
  for (const rule of ROUTE_RULES) {
    if (!rule.apps.includes(currentUser.activeApp) || seen.has(rule.pattern)) {
      continue;
    }
    seen.add(rule.pattern);
    if (rule.pattern.includes(":")) continue;
    if (isRouteAllowed(rule.pattern, currentUser)) return rule.pattern;
  }
  return null;
};

export const getVisibleNavSections = (sections, currentUser) =>
  sections
    // Secciones `hidden` (p.ej. Test drive en rollout): sin botón en navbar
    // ni drawer, pero las rutas siguen activas para trabajo interno por URL.
    .filter((section) => !section.hidden)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        isRouteAllowed(item.path, currentUser),
      ),
    }))
    .filter((section) => section.items.length > 0);
